import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import ProgressButton from 'react-progress-button';
import Validator from 'validatorjs';
import {
	isEmpty, cloneDeep, isEqual, mapValues, isArray,
} from 'lodash';
import { FormField, Loader } from '@components/common';
import { ClientService, ContactService } from '@services';
import utils, { constants } from '@utils';
import ConfirmationModal from './confirmationModal';

const initialState = {
	showModal   : false,
	editMode    : false,
	isEditable  : true,
	buttonState : '',
	form        : {
		id             : undefined,
		firstName      : '',
		lastName       : '',
		email          : '',
		phone          : '',
		address        : '',
		city           : '',
		state          : '',
		zipCode        : '',
		isAdmin        : false,
		organizationId : undefined,
		clientId       : undefined,
	},
	validations : {
		form : {
			firstName : null,
			lastName  : null,
			email     : null,
			phone     : null,
		},
	},
};

class AddContactModal extends Component {
	constructor(props) {
		super(props);
		this.state = initialState;
	}

	shouldComponentUpdate({
		withIsAdminCheckbox,
		USStates,
		client,
	}, {
		loadingPrimaryContact,
		showModal,
		editMode,
		isEditable,
		buttonState,
		form,
		validations,
	}) {
		const lastProps = this.props;
		const lastState = this.state;

		return (
			!isEqual(withIsAdminCheckbox, lastProps.withIsAdminCheckbox)
			|| !isEqual(USStates, lastProps.USStates)
			|| !isEqual(client, lastProps.client)
			|| !isEqual(loadingPrimaryContact, lastState.loadingPrimaryContact)
			|| !isEqual(showModal, lastState.showModal)
			|| !isEqual(editMode, lastState.editMode)
			|| !isEqual(isEditable, lastState.isEditable)
			|| !isEqual(buttonState, lastState.buttonState)
			|| !isEqual(form, lastState.form)
			|| !isEqual(validations, lastState.validations)
		);
	}

	closeModal = () => {
		const { updateContacts, updateOnClose } = this.props;

		this.setState({ ...initialState });

		if (updateOnClose) updateContacts();
	}

	submitForm = (event) => {
		const { onSubmit, submittedEvent } = this.props;
		const { form, editMode } = this.state;

		event.preventDefault();

		if (!this.validateForm()) {
			// Wait for thread
			setTimeout(this.updateProgressButton.bind(null, 'error'), 0);
			return;
		}

		const params = {
			...form,
			phone : utils.getDigits(form.phone),
		};

		// If there is a custom onSubmit, use it instead
		if (onSubmit) {
			onSubmit(params);
			this.closeModal();
			return;
		}

		this.updateProgressButton('loading');

		ContactService.save(params)
			.then((response) => {
				utils.notify({
					type    : 'success',
					message : `Contact successfully ${editMode ? 'updated' : 'created'}`,
				});

				this.updateProgressButton('success', () => {
					if (submittedEvent) submittedEvent(response, editMode);

					this.closeModal();
				});
			})
			.catch((error) => {
				const { response : { data, status } } = error;

				if (status === 409 && data && !isEmpty(data)) {
					this._confirmationModal.openModal({
						title              : 'Merge Contact',
						subTitle           : `${data.Message}, do you want to merge them?`,
						confirmationButton : 'Yes',
						onConfirm          : () => this.performMergeContact(data),
						onCancel           : () => this.updateProgressButton(''),
					});
					return;
				}

				this.updateProgressButton('error');

				utils.notify({
					type    : 'error',
					message : (data && data.Message) || `Error ${editMode ? 'updating' : 'creating'} contact`,
					title   : 'Oops',
				});
			});
	}

	validateForm = () => {
		const { form, validations } = this.state;

		const rules = {
			firstName : 'required',
			lastName  : 'required',
			email     : 'required|email',
			phone     : 'required|size:10',
		};

		const validator = new Validator({ ...form, phone : utils.getDigits(form.phone) }, rules);
		const result = validator.passes();

		const flatValidations = { ...validations.form, ...validator.errors.all() };
		const newValidations = { ...mapValues(flatValidations, (item) => (isArray(item) && item.length > 0 ? 'error' : null)) };

		const state = cloneDeep(this.state);
		state.validations.form = newValidations;

		this.setState(state);

		return result;
	}

	updateProgressButton = (value, callback) => {
		utils.updateProgressButton({
			context  : this,
			property : 'buttonState',
			value,
			callback,
		});
	}

	setPrimaryContact = () => {
		const { form } = this.state;
		const { updateClient, client } = this.props;
		this.setState({ loadingPrimaryContact : true });

		ClientService.save({
			...client,
			id                        : client.Id,
			PrimaryContactEmail       : form.email,
			PrimaryContactFirstName   : form.firstName,
			PrimaryContactId          : form.id,
			PrimaryContactLastName    : form.lastName,
			PrimaryContactPhoneNumber : form.phone,
		}).then(() => {
			if (updateClient) updateClient(client.Id);

			const newState = cloneDeep(this.state);
			newState.loadingPrimaryContact = false;
			if (form.id) {
				utils.notify({
					type    : 'success',
					message : 'Primary Contact updated successfully',
				});
			}
			this.setState(newState);
		}).catch((error) => {
			this.setState({ loadingPrimaryContact : false });

			if (form.id) utils.genericAPIErrorHandler('Unable to set primary contact', error);
		});
	}

	openModal = (data = {}) => {
		const state = cloneDeep(this.state);

		state.showModal = true;
		state.editMode = !!data.id;

		if (data.isEditable) state.isEditable = data.isEditable;

		if (data && !isEmpty(data)) {
			state.form.id = data.id || undefined;
			state.form.firstName = data.firstName || '';
			state.form.lastName = data.lastName || '';
			state.form.email = data.email || '';
			state.form.phone = utils.formatPhoneNumber(data.phone || '');
			state.form.address = data.address || '';
			state.form.city = data.city || '';
			state.form.state = data.state || '';
			state.form.zipCode = data.zipCode || '';
			state.form.isAdmin = data.isAdmin || false;
			state.form.organizationId = data.organizationId || undefined;
			state.form.clientId = data.clientId || undefined;
		}

		this.setState(state);
	}

	handleFormChange = (property, type) => (event) => {
		utils.genericOnFieldChangeHandler(property, type, this, event);
	}

	performMergeContact({ Id: contactId }) {
		const { form, editMode } = this.state;
		const { submittedEvent } = this.props;
		const params = {
			id       : contactId,
			clientId : form.clientId,
		};

		ContactService.mergeContact(params)
			.then((response) => {
				utils.notify({
					type    : 'success',
					message : 'Contact successfully merged',
				});

				this.updateProgressButton('success', () => {
					if (submittedEvent) submittedEvent(response, editMode);

					this.closeModal();
				});
			})
			.catch(() => {
				this.updateProgressButton('error');
				utils.notify({
					type    : 'error',
					message : 'Unable to merge contact',
				});
			});
	}

	render() {
		const {
			showModal,
			editMode,
			buttonState,
			form,
			validations,
			isEditable,
			loadingPrimaryContact,
		} = this.state;

		const {
			firstName,
			lastName,
			email,
			phone,
			address,
			city,
			state,
			zipCode,
			isAdmin,
		} = form;

		const { USStates, client, withIsAdminCheckbox } = this.props;

		return (
			<Modal
				show={showModal}
				onHide={this.closeModal}
			>
				<Modal.Header closeButton>
					<Modal.Title>
						{editMode ? 'Edit' : 'Add'}
						{' '}
						Contact
					</Modal.Title>
				</Modal.Header>
				<div className="form-horizontal">
					<Modal.Body>
						<FormField
							disabled={!isEditable}
							name="contact-modal-firstName"
							label="First Name:"
							value={firstName}
							validation={validations.form.firstName}
							onChange={this.handleFormChange('firstName', 'field')}
						/>
						<FormField
							disabled={!isEditable}
							name="contact-modal-lastName"
							label="Last Name:"
							value={lastName}
							validation={validations.form.lastName}
							onChange={this.handleFormChange('lastName', 'field')}
						/>
						<FormField
							disabled={!isEditable}
							name="contact-modal-email"
							label="Email Address:"
							value={email}
							validation={validations.form.email}
							onChange={this.handleFormChange('email', 'field')}
						/>
						<FormField
							disabled={!isEditable}
							controlType="masked"
							name="contact-modal-phone"
							label="Phone Number:"
							mask={constants.MASK_VALUE.phoneMask}
							value={phone}
							validation={validations.form.phone}
							onChange={this.handleFormChange('phone', 'field')}
						/>
						<FormField
							disabled={!isEditable}
							name="contact-modal-address"
							label="Street Address:"
							value={address}
							onChange={this.handleFormChange('address', 'field')}
						/>
						<FormField
							disabled={!isEditable}
							name="contact-modal-city"
							label="City:"
							value={city}
							onChange={this.handleFormChange('city', 'field')}
						/>
						<FormField
							disabled={!isEditable}
							name="contact-modal-state"
							controlType="select"
							label="State:"
							value={state}
							options={USStates}
							onChange={this.handleFormChange('state', 'select')}
						/>
						<FormField
							disabled={!isEditable}
							name="contact-modal-zip"
							label="Zip Code:"
							value={zipCode}
							onChange={this.handleFormChange('zipCode', 'field')}
						/>
						<If condition={withIsAdminCheckbox}>
							<FormField
								disabled={!isEditable}
								name="contact-modal-isAdmin"
								controlType="checkbox"
								label="Is Admin:"
								value={isAdmin}
								onChange={this.handleFormChange('isAdmin', 'checkbox')}
							/>
						</If>
						<Loader active={loadingPrimaryContact} />
					</Modal.Body>
					<Modal.Footer className="text-center">
						<If condition={client && editMode && (client.PrimaryContactId !== form.id)}>
							<Button
								bsStyle="primary"
								className="margin-right-15"
								onClick={this.setPrimaryContact}
							>
								Set as Primary Contact
							</Button>
						</If>
						<ProgressButton
							className="btn btn-primary btn-big margin-right-15"
							state={buttonState}
							onClick={this.submitForm}
						>
							Save
						</ProgressButton>
						<Button
							id="add-contact-modal-cancel"
							bsStyle="link"
							onClick={this.closeModal}
						>
							Cancel
						</Button>
					</Modal.Footer>
				</div>
				<ConfirmationModal ref={(ref) => { this._confirmationModal = ref; }} />
			</Modal>
		);
	}
}

AddContactModal.propTypes = {
	USStates            : PropTypes.array,
	client              : PropTypes.object,
	onSubmit            : PropTypes.func,
	submittedEvent      : PropTypes.func,
	updateClient        : PropTypes.func,
	updateContacts      : PropTypes.func,
	updateOnClose       : PropTypes.bool,
	withIsAdminCheckbox : PropTypes.bool,
};

AddContactModal.defaultProps = {
	USStates            : [],
	client              : undefined,
	onSubmit            : undefined,
	submittedEvent      : undefined,
	withIsAdminCheckbox : false,
	updateOnClose       : false,
	updateContacts      : () => {},
	updateClient        : () => {},
};

export default AddContactModal;
